import "./application";
import '../apps/login/main';
import '../apps/shareable_copy_modal/main';
import '../apps/on_boarding/main.ts';
import '../utils/helpers/datetimeLocalization';
import '../utils/contentCommentsSeen';
import '../apps/planned_trails/main.ts';
import '../apps/import_files/main';
import '../apps/recorded_trails/main.ts';
import '../apps/recording_crop_app/main.ts';
import '../apps/flyover_preview/main.ts';
import '../apps/recording_map/main.ts';
import '../apps/plan_map/main.ts';
import '../apps/purchases/main';
import '../apps/hikes/main';
import '../apps/hike_map/main.ts';
import '../apps/download_modal/main.ts';
import '../apps/mobile_login_qr/main.ts';
import '../apps/completed_hikes/main.ts';
import '../apps/flyover_videos/main.ts';
// Mounts FlyoverShare on the profile "My flyovers" page (users/flyover_videos.html.erb has the
// #flyover_share div), so the list's Share action can open the share wizard via
// window.showFlyoverShare. No-op on user pages without that div.
import '../apps/flyover_share/main.ts';
import '../apps/api_token_modal/main.ts';
// Custom Firebase action handler (/password-reset/confirm). No-ops unless that
// page's #password-reset-confirm mount point is present.
import '../apps/password_reset_confirm/main.ts';
import '../utils/share_link_modal';
import { initHikeTimeSection, initSettingsAutosave } from '../utils/settingsAutosave';
import { auth, sendPasswordResetEmail, signInWithEmailAndPassword } from "../utils/firebase/auth";
import { initAuthSync } from "../utils/firebase/authSync";
import performLogout from '../utils/auth/logout';
import { i18n } from '../i18n';

const t = i18n.global.t;

// Firebase is already initialized via the static import above.
// Call initAuthSync synchronously so onAuthStateChanged starts listening
// immediately — no dynamic-import delay.
if ((window as any).firebaseConfigs) {
  initAuthSync();
}

// Settings page: save each control as it changes and hide the fallback Save
// button. No-ops on every other user page (no [data-settings-autosave] node).
initHikeTimeSection();
initSettingsAutosave();

const userLogoutButton = document.getElementById('user-logout-button')

if (userLogoutButton) {
  userLogoutButton.addEventListener('click', (e) => {
    e.preventDefault();
    performLogout()
      .then(() => {
        window.location.href = '/login';
      })
      .catch((error) => {
        console.error('Error during logout:', error);
        window.location.href = '/logout';
      });
  });
}

const emailInput = document.getElementById('email');
const resetButton = document.getElementById('reset-button');
const successMessage = document.getElementById('success-message');
const errorMessage = document.getElementById('error-message');
const errorText = document.getElementById('error-text');
const hiikerApiAccountSetupContainer = document.getElementById('hiiker-api-account-setup');
const hiikerApiAccountSetupPasswordInput = document.getElementById('account-setup-password') as HTMLInputElement | null;
const hiikerApiAccountSetupPasswordConfirmationInput = document.getElementById('account-setup-password-confirmation') as HTMLInputElement | null;
const hiikerApiAccountSetupButton = document.getElementById('account-setup-button') as HTMLButtonElement | null;
const hiikerApiAccountSetupSuccessMessage = document.getElementById('account-setup-success-message');
const hiikerApiAccountSetupErrorMessage = document.getElementById('account-setup-error-message');
const hiikerApiAccountSetupErrorText = document.getElementById('account-setup-error-text');

if (emailInput && resetButton && successMessage && errorMessage && errorText) {
  resetButton.addEventListener('click', async function() {
    const email = emailInput.value.trim();

    if (!email) {
      showError(t('entrypoints.users.enter_your_email'));
      return;
    }

    if (!isValidEmail(email)) {
      showError(t('entrypoints.users.enter_a_valid_email'));
      return;
    }

    resetButton.disabled = true;
    resetButton.textContent = t('entrypoints.users.sending');

    try {

      await sendPasswordResetEmail(auth, email);

      // Hide any error messages
      errorMessage.style.display = 'none';

      // Show success message
      successMessage.style.display = 'block';

      // Clear the email input
      emailInput.value = '';

      // Reset button state
      resetButton.disabled = false;
      resetButton.textContent = t('users.password_reset.send_reset_email');

    } catch (error) {
      console.error('Password reset error:', error);

      let errorMsg = t('entrypoints.users.an_error_occurred');

      if (error.code === 'auth/user-not-found') {
        errorMsg = t('entrypoints.users.no_account_found');
      } else if (error.code === 'auth/invalid-email') {
        errorMsg = t('entrypoints.users.invalid_email');
      } else if (error.code === 'auth/too-many-requests') {
        errorMsg = t('entrypoints.users.too_many_requests');
      }

      showError(errorMsg);

      // Reset button state
      resetButton.disabled = false;
      resetButton.textContent = t('users.password_reset.send_reset_email');
    }
  });

// Allow Enter key to submit
  emailInput.addEventListener('keypress', function(e) {
    if (e.key === 'Enter') {
      resetButton.click();
    }
  });

  function isValidEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  function showError(message) {
    errorText.textContent = message;
    errorMessage.style.display = 'block';
    successMessage.style.display = 'none';
  }
}

if (
  hiikerApiAccountSetupContainer &&
  hiikerApiAccountSetupPasswordInput &&
  hiikerApiAccountSetupPasswordConfirmationInput &&
  hiikerApiAccountSetupButton &&
  hiikerApiAccountSetupSuccessMessage &&
  hiikerApiAccountSetupErrorMessage &&
  hiikerApiAccountSetupErrorText
) {
  const email = hiikerApiAccountSetupContainer.getAttribute('data-email') || '';
  const token = hiikerApiAccountSetupContainer.getAttribute('data-token') || '';

  hiikerApiAccountSetupButton.addEventListener('click', async function() {
    const password = hiikerApiAccountSetupPasswordInput.value;
    const passwordConfirmation = hiikerApiAccountSetupPasswordConfirmationInput.value;

    if (!token || !email) {
      showHiikerApiAccountSetupError(t('entrypoints.users.setup_link_invalid'));
      return;
    }

    if (password.length < 6) {
      showHiikerApiAccountSetupError(t('entrypoints.users.password_min_length'));
      return;
    }

    if (password !== passwordConfirmation) {
      showHiikerApiAccountSetupError(t('entrypoints.users.password_confirmation_mismatch'));
      return;
    }

    hiikerApiAccountSetupButton.disabled = true;
    hiikerApiAccountSetupButton.textContent = t('entrypoints.users.setting_up');
    hiikerApiAccountSetupErrorMessage.style.display = 'none';
    hiikerApiAccountSetupSuccessMessage.style.display = 'none';

    try {
      const response = await fetch('/user/hiiker-api/account-setup', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password,
          password_confirmation: passwordConfirmation
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('entrypoints.users.could_not_finish_setup'));
      }

      hiikerApiAccountSetupSuccessMessage.textContent = data.message || t('users.hiiker_api_account_setup.password_set_successfully_signing_you_in');
      hiikerApiAccountSetupSuccessMessage.style.display = 'block';
      try {
        await signInAfterHiikerApiAccountSetup(email, password);
      } catch (error: any) {
        console.error('Automatic sign-in after HiiKER API account setup failed:', error);
        showHiikerApiAccountSetupError(t('entrypoints.users.password_set_sign_in_failed'));
        window.setTimeout(() => {
          window.location.assign(data.login_url || '/login');
        }, 1500);
      }
    } catch (error: any) {
      console.error('HiiKER API account setup error:', error);
      showHiikerApiAccountSetupError(error.message || t('entrypoints.users.an_error_occurred'));
      hiikerApiAccountSetupButton.disabled = false;
      hiikerApiAccountSetupButton.textContent = t('users.hiiker_api_account_setup.set_password_sign_in');
    }
  });

  hiikerApiAccountSetupPasswordConfirmationInput.addEventListener('keypress', function(e) {
    if (e.key === 'Enter') {
      hiikerApiAccountSetupButton.click();
    }
  });

  function showHiikerApiAccountSetupError(message) {
    hiikerApiAccountSetupErrorText.textContent = message;
    hiikerApiAccountSetupErrorMessage.style.display = 'block';
    hiikerApiAccountSetupSuccessMessage.style.display = 'none';
  }
}

async function signInAfterHiikerApiAccountSetup(email: string, password: string) {
  let lastError = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken(true);
      window.location.assign(`/firebase/auth?accessToken=${encodeURIComponent(idToken)}`);
      return;
    } catch (error) {
      lastError = error;
      await waitForHiikerApiAccountSetupSync();
    }
  }

  throw lastError;
}

function waitForHiikerApiAccountSetupSync() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 600);
  });
}
